import { useCallback, useEffect, useState } from 'react';

import { api, type VarolistaValasz, type VarolistaSor } from '../api.ts';
import { szam } from '../formazas.ts';

/**
 * A régi Excelből még be nem hozott, döntésre váró állandó címek.
 *
 * Az iroda rövidítéseket írt a napi lapokra („Food Revo", „PP Pólus"), amikhez
 * több étterem is illik — ilyet a gép nem talál ki. Itt lehet eldönteni, melyik
 * a jó. **A döntést a program megjegyzi**, így ugyanaz a név legközelebb
 * magától a helyére kerül.
 *
 * Ha a forrás Excel már nincs meg, a panel nem jelenik meg — a munka akkor
 * már úgyis kész.
 */
export function Varolista({
  nyitva,
  onNyit,
  onDarab,
  darab,
  onValtozott,
}: {
  nyitva: boolean;
  onNyit: (b: boolean) => void;
  onDarab: (n: number | null) => void;
  darab: number | null;
  onValtozott: () => void;
}) {
  const [lista, setLista] = useState<VarolistaValasz | null>(null);
  const [hiba, setHiba] = useState<string | null>(null);
  const [munkaban, setMunkaban] = useState<string | null>(null);
  const [valasztasok, setValasztasok] = useState<Record<string, number>>({});

  const betolt = useCallback(async () => {
    try {
      const eredmeny = await api.get<VarolistaValasz>('/api/allando/varolista');
      setLista(eredmeny);
      onDarab(eredmeny.elerheto ? eredmeny.sorok.length : null);
    } catch (h) {
      setHiba(h instanceof Error ? h.message : 'Nem sikerült betölteni a várólistát.');
      onDarab(null);
    }
  }, [onDarab]);

  useEffect(() => {
    void betolt();
  }, [betolt]);

  if (darab === null || darab === 0) return null;

  const kulcs = (s: VarolistaSor) => `${s.lap}#${s.excel_sor}`;

  async function dontes(s: VarolistaSor, etterem_id: number) {
    setMunkaban(kulcs(s));
    setHiba(null);
    try {
      await api.post('/api/allando/varolista/dontes', {
        lap: s.lap,
        excel_sor: s.excel_sor,
        etterem_id,
      });
      await betolt();
      onValtozott();
    } catch (h) {
      setHiba(h instanceof Error ? h.message : 'A párosítás nem sikerült.');
    } finally {
      setMunkaban(null);
    }
  }

  async function elvet(s: VarolistaSor) {
    if (!confirm(`Biztosan kihagyod? — "${s.fajlbeli_nev}" (${s.nap_neve})`)) return;
    setMunkaban(kulcs(s));
    try {
      await api.post('/api/allando/varolista/elvetes', {
        lap: s.lap,
        excel_sor: s.excel_sor,
        fajlbeli_nev: s.fajlbeli_nev,
      });
      setLista((elozo) =>
        elozo
          ? { ...elozo, sorok: elozo.sorok.filter((x) => kulcs(x) !== kulcs(s)) }
          : elozo,
      );
      onDarab(darab! - 1);
    } finally {
      setMunkaban(null);
    }
  }

  return (
    <div className="varolista">
      <button className="varolista-fej" onClick={() => onNyit(!nyitva)}>
        <span className="jel">{darab}</span>
        <b>állandó cím vár döntésre a régi Excelből</b>
        <span className="hint">
          Az irodán rövidítve írták a nevüket, ezért nem egyértelmű, melyik étteremhez
          tartoznak.
        </span>
        <span className="nyil">{nyitva ? '▲' : '▼'}</span>
      </button>

      {nyitva && (
        <div className="varolista-test">
          {hiba && <div className="uzenet bad">{hiba}</div>}

          <table className="tt2">
            <thead>
              <tr>
                <th>Nap</th>
                <th>Ez áll a régi fájlban</th>
                <th className="n">MO</th>
                <th className="n">FO</th>
                <th className="n">Mennyiség</th>
                <th>Melyik étterem ez?</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {(lista?.sorok ?? []).map((s) => {
                const k = kulcs(s);
                const dolgozik = munkaban === k;
                return (
                  <tr key={k}>
                    <td className="calc2" style={{ textAlign: 'left' }}>
                      {s.nap_neve}
                    </td>
                    <td className="nm3">
                      <b>{s.fajlbeli_nev}</b>
                      {s.cim && <small>{s.cim}</small>}
                      {s.tobbertelmu && (
                        <small className="only">több étterem is illik rá</small>
                      )}
                    </td>
                    <td className="calc2">{szam(s.mo, 0)}</td>
                    <td className="calc2">{szam(s.fo, 0)}</td>
                    <td className="calc2">{szam(s.mennyiseg, 0)}</td>
                    <td>
                      {s.jeloltek.length > 0 ? (
                        <select
                          value={valasztasok[k] ?? ''}
                          disabled={dolgozik}
                          onChange={(e) =>
                            setValasztasok({ ...valasztasok, [k]: Number(e.target.value) })
                          }
                        >
                          <option value="">— válassz —</option>
                          {s.jeloltek.map((j) => (
                            <option key={j.etterem_id} value={j.etterem_id}>
                              {j.nev} ({Math.round(j.pontszam * 100)}%)
                            </option>
                          ))}
                        </select>
                      ) : (
                        <span className="hint">nincs javaslat — keresd meg a törzsben</span>
                      )}
                    </td>
                    <td className="act" style={{ width: 150, whiteSpace: 'nowrap' }}>
                      <button
                        className="btn sm pri"
                        disabled={dolgozik || !valasztasok[k]}
                        onClick={() => void dontes(s, valasztasok[k]!)}
                      >
                        Ez az
                      </button>{' '}
                      <button
                        className="btn sm"
                        disabled={dolgozik}
                        title="Ez a sor nem tartozik egyik étteremhez sem"
                        onClick={() => void elvet(s)}
                      >
                        Kihagy
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          <p className="hint" style={{ marginTop: 10 }}>
            A döntést a program megjegyzi: ugyanezt a nevet legközelebb magától
            felismeri, a napi járatlapok importálásánál is.
          </p>
        </div>
      )}
    </div>
  );
}
